import React, { useState, useContext } from 'react';
import { AppContext } from '../../context/AppContext';
import { ShieldAlert, Users, ClipboardList, MinusCircle, Check, X, AlertCircle, Calendar, FileText, ExternalLink, RefreshCw, Receipt, CreditCard, Download, DollarSign } from 'lucide-react';

const LeaveApprovalManagement = () => {
  const { users, leaveHistory, adminForceDeduct, reconcileLeaveFromAuditLogs, approvals, handleApprovalAction, currentUser, getAutoApprovalLine } = useContext(AppContext);
  const [filterNegative, setFilterNegative] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState('approvals'); // 'approvals' | 'balances' | 'audit' | 'history' | 'expenseHistory'
  const [historySearchTerm, setHistorySearchTerm] = useState('');
  const [historyTypeFilter, setHistoryTypeFilter] = useState('ALL');

  // Expense History SubTab State & Date Range Helpers
  const getFirstDayOfMonth = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}-01`;
  };

  const getLastDayOfMonth = (date) => {
    const y = date.getFullYear();
    const m = date.getMonth();
    const lastDay = new Date(y, m + 1, 0).getDate();
    return `${y}-${String(m + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  };

  const today = new Date();
  const [expenseStartDate, setExpenseStartDate] = useState(getFirstDayOfMonth(today));
  const [expenseEndDate, setExpenseEndDate] = useState(getLastDayOfMonth(today));
  const [expenseDatePreset, setExpenseDatePreset] = useState('THIS_MONTH'); // 'THIS_MONTH' | 'LAST_MONTH' | 'LAST_3_MONTHS' | 'THIS_YEAR' | 'ALL' | 'CUSTOM'

  const [expenseSearchTerm, setExpenseSearchTerm] = useState('');
  const [expenseStatusFilter, setExpenseStatusFilter] = useState('ALL');
  const [expenseCategoryFilter, setExpenseCategoryFilter] = useState('ALL');
  const [expenseCardFilter, setExpenseCardFilter] = useState('ALL');

  // Leave History Date Range Filter State
  const [historyStartDate, setHistoryStartDate] = useState('');
  const [historyEndDate, setHistoryEndDate] = useState('');
  const [historyDatePreset, setHistoryDatePreset] = useState('ALL'); // 'THIS_MONTH' | 'LAST_MONTH' | 'LAST_3_MONTHS' | 'THIS_YEAR' | 'ALL' | 'CUSTOM'

  // Pagination states (20 items per page)
  const ITEMS_PER_PAGE = 20;
  const [historyPage, setHistoryPage] = useState(1);
  const [expensePage, setExpensePage] = useState(1);
  const [combinedPage, setCombinedPage] = useState(1);

  const handleDatePresetClick = (preset) => {
    setExpenseDatePreset(preset);
    setExpensePage(1);
    const now = new Date();
    if (preset === 'THIS_MONTH') {
      setExpenseStartDate(getFirstDayOfMonth(now));
      setExpenseEndDate(getLastDayOfMonth(now));
    } else if (preset === 'LAST_MONTH') {
      const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      setExpenseStartDate(getFirstDayOfMonth(prevMonth));
      setExpenseEndDate(getLastDayOfMonth(prevMonth));
    } else if (preset === 'LAST_3_MONTHS') {
      const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, 1);
      setExpenseStartDate(getFirstDayOfMonth(threeMonthsAgo));
      setExpenseEndDate(getLastDayOfMonth(now));
    } else if (preset === 'THIS_YEAR') {
      setExpenseStartDate(`${now.getFullYear()}-01-01`);
      setExpenseEndDate(`${now.getFullYear()}-12-31`);
    } else if (preset === 'ALL') {
      setExpenseStartDate('');
      setExpenseEndDate('');
    }
  };

  const handleHistoryDatePresetClick = (preset) => {
    setHistoryDatePreset(preset);
    setHistoryPage(1);
    const now = new Date();
    if (preset === 'THIS_MONTH') {
      setHistoryStartDate(getFirstDayOfMonth(now));
      setHistoryEndDate(getLastDayOfMonth(now));
    } else if (preset === 'LAST_MONTH') {
      const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      setHistoryStartDate(getFirstDayOfMonth(prevMonth));
      setHistoryEndDate(getLastDayOfMonth(prevMonth));
    } else if (preset === 'LAST_3_MONTHS') {
      const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, 1);
      setHistoryStartDate(getFirstDayOfMonth(threeMonthsAgo));
      setHistoryEndDate(getLastDayOfMonth(now));
    } else if (preset === 'THIS_YEAR') {
      setHistoryStartDate(`${now.getFullYear()}-01-01`);
      setHistoryEndDate(`${now.getFullYear()}-12-31`);
    } else if (preset === 'ALL') {
      setHistoryStartDate('');
      setHistoryEndDate('');
    }
  };

  // Detail Modal State
  const [selectedDetailApproval, setSelectedDetailApproval] = useState(null);

  const combinedHistory = [
    // 1. Map all leave approvals (PENDING, APPROVED, REJECTED)
    ...approvals.filter(a => a.Type === 'LEAVE').map(a => ({
      HistoryID: a.ApprovalID,
      UserID: a.ApplicantID,
      Timestamp: a.CreatedAt,
      ChangeType: a.Status === 'APPROVED' ? 'LEAVE_APPROVED' : a.Status === 'REJECTED' ? 'LEAVE_REJECTED' : 'LEAVE_PENDING',
      Days: -Number(a.LeaveDays || 0),
      Reason: a.Details,
      CreatedBy: a.ApplicantName,
      isApproval: true,
      approvalItem: a,
      proofUrl: a.ProofUrl || a.ReceiptS3Url,
      rejectReason: a.RejectReason,
      status: a.Status
    })),
    // 2. Map direct balance adjustments (ANNUAL_GRANT, ADMIN_FORCE_DEDUCT)
    ...leaveHistory.filter(h => h.ChangeType !== 'LEAVE_APPROVED').map(h => ({
      HistoryID: h.HistoryID,
      UserID: h.UserID,
      Timestamp: h.Timestamp,
      ChangeType: h.ChangeType,
      Days: Number(h.Days),
      Reason: h.Reason,
      CreatedBy: h.CreatedBy,
      isApproval: false
    }))
  ];

  const searchedHistoryList = combinedHistory.filter(item => {
    // 1. Date Range Filter
    if (historyStartDate || historyEndDate) {
      const itemDate = item.Timestamp ? item.Timestamp.slice(0, 10) : '';
      if (historyStartDate && itemDate && itemDate < historyStartDate) return false;
      if (historyEndDate && itemDate && itemDate > historyEndDate) return false;
    }
    const targetUser = users.find(u => u.UserID === item.UserID) || {};
    const nameMatch = (targetUser.Name || '').toLowerCase().includes(historySearchTerm.toLowerCase());
    const deptMatch = (targetUser.Department || '').toLowerCase().includes(historySearchTerm.toLowerCase());
    const searchMatch = !historySearchTerm || nameMatch || deptMatch;
    const typeMatch = historyTypeFilter === 'ALL' || item.ChangeType === historyTypeFilter;
    return searchMatch && typeMatch;
  }).sort((a, b) => new Date(b.Timestamp) - new Date(a.Timestamp));

  // Expense Claims & History Computations (Filtered by Date Range First)
  const allExpenseClaims = approvals.filter(a => a.Type === 'EXPENSE');

  // 1. Apply Date Range Filter
  const dateFilteredExpenseClaims = allExpenseClaims.filter(item => {
    if (!expenseStartDate && !expenseEndDate) return true;
    const itemDate = item.CreatedAt ? item.CreatedAt.slice(0, 10) : '';
    if (expenseStartDate && itemDate && itemDate < expenseStartDate) return false;
    if (expenseEndDate && itemDate && itemDate > expenseEndDate) return false;
    return true;
  });

  // 2. Metrics computed from dateFilteredExpenseClaims for selected period
  const totalExpenseClaimsCount = dateFilteredExpenseClaims.length;
  const totalExpenseClaimsAmount = dateFilteredExpenseClaims.reduce((sum, a) => sum + Number(a.Amount || 0), 0);
  const approvedExpenseClaimsAmount = dateFilteredExpenseClaims.filter(a => a.Status === 'APPROVED').reduce((sum, a) => sum + Number(a.Amount || 0), 0);
  const pendingExpenseClaimsAmount = dateFilteredExpenseClaims.filter(a => a.Status === 'PENDING').reduce((sum, a) => sum + Number(a.Amount || 0), 0);
  const rejectedExpenseClaimsAmount = dateFilteredExpenseClaims.filter(a => a.Status === 'REJECTED').reduce((sum, a) => sum + Number(a.Amount || 0), 0);

  // 3. Apply Search & Categorical Filters
  const searchedExpenseList = dateFilteredExpenseClaims.filter(item => {
    const targetUser = users.find(u => u.UserID === item.ApplicantID) || {};
    const nameMatch = (item.ApplicantName || targetUser.Name || '').toLowerCase().includes(expenseSearchTerm.toLowerCase());
    const deptMatch = (item.ApplicantDept || targetUser.Department || '').toLowerCase().includes(expenseSearchTerm.toLowerCase());
    const detailsMatch = (item.Details || '').toLowerCase().includes(expenseSearchTerm.toLowerCase());
    const searchMatch = !expenseSearchTerm || nameMatch || deptMatch || detailsMatch;

    const statusMatch = expenseStatusFilter === 'ALL' || item.Status === expenseStatusFilter;
    
    // Category Match
    let categoryMatch = true;
    if (expenseCategoryFilter !== 'ALL') {
      categoryMatch = (item.Details || '').includes(`[${expenseCategoryFilter}]`);
    }

    // Card Match
    let cardMatch = true;
    if (expenseCardFilter !== 'ALL') {
      cardMatch = (item.CardCompany === expenseCardFilter) || (item.Details || '').includes(expenseCardFilter);
    }

    return searchMatch && statusMatch && categoryMatch && cardMatch;
  }).sort((a, b) => new Date(b.CreatedAt) - new Date(a.CreatedAt));

  // Export Expense History to CSV
  const exportExpenseToCsv = () => {
    if (searchedExpenseList.length === 0) {
      alert('내보낼 경비 결재 내역이 없습니다.');
      return;
    }
    const headers = ['신청일시', '사원명', '소속부서', '직급', '경비분류', '사용처/가맹점', '결제금액', '결제카드', '상태'];
    const rows = searchedExpenseList.map(item => {
      const targetUser = users.find(u => u.UserID === item.ApplicantID) || {};
      const category = item.Details?.match(/\[(.*?)\]/)?.[1] || '경비';
      const cleanDetails = item.Details?.replace(/\[(.*?)\]/, '').trim() || item.Details;
      const statusText = item.Status === 'APPROVED' ? '승인 완료' : item.Status === 'REJECTED' ? '반려' : '대기 중';
      return [
        new Date(item.CreatedAt).toLocaleString(),
        item.ApplicantName || targetUser.Name || '',
        item.ApplicantDept || targetUser.Department || '',
        targetUser.JobTitle || '',
        category,
        `"${(cleanDetails || '').replace(/"/g, '""')}"`,
        item.Amount || 0,
        item.CardCompany ? `${item.CardCompany} ${item.CardNumber || ''}` : '',
        statusText
      ];
    });
    
    const periodStr = expenseStartDate ? `${expenseStartDate}~${expenseEndDate || '현재'}` : '전체기간';
    const csvContent = '\uFEFF' + [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `전체_경비_결재_이력_${periodStr}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Approval stages helpers for enforcing sequential approvals (Requirement 3)
  const getApprovalStages = (appr) => {
    if (appr.ApprovalStages && appr.ApprovalStages.length > 0) {
      return appr.ApprovalStages;
    }
    if (getAutoApprovalLine) {
      return getAutoApprovalLine({ UserID: appr.ApplicantID, Department: appr.ApplicantDept || '인사팀' });
    }
    return [];
  };

  const getActiveStage = (appr) => {
    const stages = getApprovalStages(appr);
    const currentStep = appr.CurrentStep || 1;
    return stages.find(s => s.Step === currentStep);
  };

  const isCurrentApprover = (appr) => {
    if (!appr) return false;
    if (appr.Status === 'CANCEL_REQUESTED') {
      if (currentUser?.Role === 'ADMIN') return true;
      return appr.ApprovalStages?.some(s => s.ApproverID === currentUser?.UserID || s.ApproverName === currentUser?.Name);
    }
    if (appr.Status !== 'PENDING') return false;
    const activeStage = getActiveStage(appr);
    if (!activeStage) return false;
    return activeStage.ApproverID === currentUser?.UserID || activeStage.ApproverName === currentUser?.Name;
  };

  // Deduct Modal state
  const [isDeducting, setIsDeducting] = useState(false);
  const [deductUserId, setDeductUserId] = useState('');
  const [deductDays, setDeductDays] = useState('');
  const [deductReason, setDeductReason] = useState('');

  // Reject Modal state
  const [rejectingApprId, setRejectingApprId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const handleDeductSubmit = (e) => {
    e.preventDefault();
    if (!deductUserId || !deductDays || !deductReason) {
      alert('모든 필수 항목을 입력해 주세요.');
      return;
    }

    const days = parseFloat(deductDays);
    if (isNaN(days) || days === 0) {
      alert('일수를 올바르게 입력해 주세요 (0이 아닌 정수/실수).');
      return;
    }

    adminForceDeduct(deductUserId, days, deductReason);
    
    // Reset
    setDeductUserId('');
    setDeductDays('');
    setDeductReason('');
    setIsDeducting(false);
    
    alert('관리자 직권으로 연차 차감/가산 처리가 완료되었으며, 감사 로그 및 DynamoDB에 영구 기록되었습니다.');
  };

  // Reconcile and Sync all force deduction logs with employee balances and DynamoDB
  const handleReconcileFromAuditLogs = async () => {
    try {
      const res = await reconcileLeaveFromAuditLogs();
      if (res.success) {
        alert(res.message);
      } else {
        alert(`동기화 실패: ${res.error}`);
      }
    } catch (err) {
      alert(`동기화 중 오류 발생: ${err.message}`);
    }
  };

  // Filter users with negative remaining leave
  const filteredUsers = filterNegative ? users.filter(u => u.RemainingLeave < 0) : users;

  // Filter LeaveHistory for ADMIN_FORCE_DEDUCT logs
  const forceDeductLogs = leaveHistory.filter(h => h.ChangeType === 'ADMIN_FORCE_DEDUCT');

  // Filter pending approvals: Admin sees everything, approvers see only their active step
  const pendingApprovals = approvals.filter(a => {
    if (a.Status !== 'PENDING' && a.Status !== 'CANCEL_REQUESTED') return false;
    if (currentUser?.Role === 'ADMIN') return true;

    if (a.Status === 'CANCEL_REQUESTED') {
      return a.ApprovalStages?.some(s => s.ApproverID === currentUser?.UserID || s.ApproverName === currentUser?.Name);
    }

    // For non-admin approvers, check if they are the current step's approver
    const currentStep = a.CurrentStep || 1;
    const currentStage = a.ApprovalStages?.find(s => s.Step === currentStep);
    if (!currentStage) return false;

    return currentStage.ApproverID === currentUser?.UserID || currentStage.ApproverName === currentUser?.Name;
  });

  return (
    <div className="space-y-6 text-left">
      {/* Sub navigation tabs (Only visible to ADMIN role) */}
      {currentUser?.Role === 'ADMIN' && (
        <div className="flex flex-wrap border-b border-slate-200 pb-px gap-1">
          <button
            onClick={() => setActiveSubTab('approvals')}
            className={`flex items-center gap-2 px-5 py-3.5 border-b-2 font-bold text-sm md:text-base transition-all cursor-pointer ${
              activeSubTab === 'approvals'
                ? 'border-indigo-600 text-indigo-700 bg-indigo-50/50 rounded-t-xl'
                : 'border-transparent text-slate-500 hover:text-slate-900'
            }`}
          >
            <ClipboardList className="w-4 h-4 md:w-5 md:h-5" />
            신청 대기 결재선 ({pendingApprovals.length}건)
          </button>
          <button
            onClick={() => setActiveSubTab('balances')}
            className={`flex items-center gap-2 px-5 py-3.5 border-b-2 font-bold text-sm md:text-base transition-all cursor-pointer ${
              activeSubTab === 'balances'
                ? 'border-indigo-600 text-indigo-700 bg-indigo-50/50 rounded-t-xl'
                : 'border-transparent text-slate-500 hover:text-slate-900'
            }`}
          >
            <Users className="w-4 h-4 md:w-5 md:h-5" />
            직원 연차 현황 목록
          </button>
          <button
            onClick={() => setActiveSubTab('audit')}
            className={`flex items-center gap-2 px-5 py-3.5 border-b-2 font-bold text-sm md:text-base transition-all cursor-pointer ${
              activeSubTab === 'audit'
                ? 'border-indigo-600 text-indigo-700 bg-indigo-50/50 rounded-t-xl'
                : 'border-transparent text-slate-500 hover:text-slate-900'
            }`}
          >
            <ShieldAlert className="w-4 h-4 md:w-5 md:h-5" />
            직권 차감 감사 로그
          </button>
          <button
            onClick={() => setActiveSubTab('history')}
            className={`flex items-center gap-2 px-5 py-3.5 border-b-2 font-bold text-sm md:text-base transition-all cursor-pointer ${
              activeSubTab === 'history'
                ? 'border-indigo-600 text-indigo-700 bg-indigo-50/50 rounded-t-xl'
                : 'border-transparent text-slate-500 hover:text-slate-900'
            }`}
          >
            <ClipboardList className="w-4 h-4 md:w-5 md:h-5" />
            전체 연차 신청/이력 조회
          </button>
          <button
            onClick={() => setActiveSubTab('expenseHistory')}
            className={`flex items-center gap-2 px-5 py-3.5 border-b-2 font-bold text-sm md:text-base transition-all cursor-pointer ${
              activeSubTab === 'expenseHistory'
                ? 'border-indigo-600 text-indigo-700 bg-indigo-50/50 rounded-t-xl'
                : 'border-transparent text-slate-500 hover:text-slate-900'
            }`}
          >
            <Receipt className="w-4 h-4 md:w-5 md:h-5 text-emerald-600" />
            전체 경비 결재요청/이력 조회
          </button>
        </div>
      )}

      {/* 1. Pending Approvals Queue Tab (Requirements 4 & 5) */}
      {activeSubTab === 'approvals' && (
        <div className="bg-white border border-slate-200 rounded-2xl p-4 md:p-6 shadow-xs space-y-6">
          <div>
            <h3 className="text-base md:text-lg font-bold text-slate-900">연차 & 경비 결재 요청 큐</h3>
            <p className="text-xs md:text-sm text-slate-500 mt-1">직원들이 청구한 연차 및 경비 정산 내역입니다. 증빙 서류를 검증 후 결재 처리를 진행하세요.</p>
          </div>

          {/* A. Mobile View: Responsive Card Stack (No Horizontal Scroll!) (Requirement 4) */}
          <div className="block md:hidden space-y-4">
            {pendingApprovals.length === 0 ? (
              <div className="text-center py-12 text-slate-400 text-sm bg-slate-50 rounded-2xl border border-slate-200">
                결재 대기 중인 요청이 없습니다.
              </div>
            ) : (
              pendingApprovals.map(appr => (
                <div key={appr.ApprovalID} className="bg-slate-50/70 border border-slate-200 rounded-2xl p-4 space-y-3.5 shadow-2xs">
                  {/* Top: Applicant & Type Badge */}
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="text-base font-bold text-slate-900 block">{appr.ApplicantName}</span>
                      <span className="text-xs text-slate-400 mt-0.5 block">{new Date(appr.CreatedAt).toLocaleDateString()} 신청</span>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className={`px-2.5 py-1 rounded-lg text-xs font-bold ${
                        appr.Type === 'LEAVE' ? 'bg-indigo-100 text-indigo-800 border border-indigo-200' : 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                      }`}>
                        {appr.Type === 'LEAVE' ? '휴가/연차' : '경비청구'}
                      </span>
                      {appr.Status === 'CANCEL_REQUESTED' && (
                        <span className="px-2 py-0.5 rounded bg-rose-100 text-rose-800 border border-rose-300 text-[10px] font-bold">
                          취소 요청
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Body: Details & Amount */}
                  <div className="bg-white p-3.5 rounded-xl border border-slate-200 text-sm space-y-2">
                    <p className="font-semibold text-slate-800 leading-snug">{appr.Details}</p>
                    {appr.Amount > 0 && (
                      <p className="text-base font-extrabold text-indigo-700">
                        청구 금액: ₩ {appr.Amount.toLocaleString()}
                      </p>
                    )}
                    {appr.Type === 'EXPENSE' && appr.CardCompany && (
                      <p className="text-xs text-slate-500">
                        결제: {appr.CardCompany} ({appr.CardNumber})
                      </p>
                    )}

                    {/* Multi-step Approval Stages Progress (Requirement 3) */}
                    {appr.ApprovalStages && appr.ApprovalStages.length > 0 && (
                      <div className="pt-2 border-t border-slate-100 space-y-1.5">
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">
                          결재 진행 라인 ({appr.CurrentStep || 1} / {appr.ApprovalStages.length}단계):
                        </span>
                        <div className="flex items-center gap-1 flex-wrap text-xs">
                          {appr.ApprovalStages.map((st, sIdx) => (
                            <div key={sIdx} className="flex items-center gap-1">
                              <span className={`px-2 py-0.5 rounded-md text-[11px] font-bold ${
                                st.Status === 'APPROVED' ? 'bg-emerald-100 text-emerald-800' :
                                st.Status === 'PENDING' ? 'bg-amber-100 text-amber-800 ring-1 ring-amber-400 font-extrabold' :
                                'bg-slate-100 text-slate-400'
                              }`}>
                                {st.Status === 'APPROVED' ? '✓ ' : st.Status === 'PENDING' ? '⏳ ' : ''}
                                {st.Step}차: {st.ApproverName} ({st.JobTitle})
                              </span>
                              {sIdx < appr.ApprovalStages.length - 1 && <span className="text-slate-300 text-[10px]">➔</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Proof Document Link */}
                    {(appr.ProofUrl || appr.ReceiptS3Url) && (
                      <div className="pt-2 border-t border-slate-100">
                        <a
                          href={appr.ProofUrl || appr.ReceiptS3Url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs text-indigo-600 hover:underline font-bold"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          <span>증빙/영수증 문서 확인하기</span>
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Bottom: Perfectly Aligned Full-width Action Buttons (Requirement 5 & 3) */}
                  <div className="pt-1">
                    {isCurrentApprover(appr) ? (
                      <div className="flex items-center gap-3 w-full">
                        <button
                          onClick={() => handleApprovalAction(appr.ApprovalID, 'APPROVED')}
                          className="flex-1 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm rounded-xl shadow-sm transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                        >
                          <Check className="w-4 h-4" />
                          <span>{appr.Status === 'CANCEL_REQUESTED' ? '취소 승인' : '승인'}</span>
                        </button>
                        <button
                          onClick={() => { setRejectingApprId(appr.ApprovalID); setRejectReason(''); }}
                          className="flex-1 py-3 bg-rose-600 hover:bg-rose-700 text-white font-bold text-sm rounded-xl shadow-sm transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                        >
                          <X className="w-4 h-4" />
                          <span>{appr.Status === 'CANCEL_REQUESTED' ? '취소 반려' : '반려'}</span>
                        </button>
                      </div>
                    ) : (
                      <div className="w-full py-2.5 bg-slate-100 text-slate-500 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 border border-slate-200">
                        <AlertCircle className="w-3.5 h-3.5 text-slate-400" />
                        <span>결재 순서 대기 중 ({getActiveStage(appr)?.ApproverName} 차례)</span>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* B. Desktop View: Spacious Data Table (Requirement 5 & 7) */}
          <div className="hidden md:block overflow-x-auto border border-slate-200 rounded-xl">
            <table className="w-full text-left text-sm text-slate-800">
              <thead className="bg-slate-50 text-xs font-bold text-slate-500 uppercase tracking-wider">
                <tr>
                  <th className="px-5 py-3.5 whitespace-nowrap">신청자</th>
                  <th className="px-4 py-3.5 whitespace-nowrap">유형</th>
                  <th className="px-5 py-3.5">세부 내역</th>
                  <th className="px-4 py-3.5 whitespace-nowrap">결재 진행 라인</th>
                  <th className="px-4 py-3.5 whitespace-nowrap">금액 / 일수</th>
                  <th className="px-4 py-3.5 whitespace-nowrap">신청일</th>
                  <th className="px-4 py-3.5 whitespace-nowrap">증빙 서류</th>
                  <th className="px-5 py-3.5 text-right whitespace-nowrap">결재 처리</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pendingApprovals.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-12 text-slate-400">결재 대기 중인 항목이 없습니다.</td>
                  </tr>
                ) : (
                  pendingApprovals.map(appr => {
                    const targetUser = users.find(u => u.UserID === appr.ApplicantID || u.Name === appr.ApplicantName);
                    const totalL = (targetUser?.TotalLeave !== undefined && targetUser?.TotalLeave !== null && targetUser?.TotalLeave !== '') ? Number(targetUser.TotalLeave) : 15;
                    const usedL = Number(targetUser?.UsedLeave || 0);
                    const remL = (targetUser?.RemainingLeave !== undefined && targetUser?.RemainingLeave !== null && targetUser?.RemainingLeave !== '') ? Number(targetUser.RemainingLeave) : (totalL - usedL);

                    return (
                    <tr key={appr.ApprovalID} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-4 whitespace-nowrap">
                        <div className="font-bold text-slate-900">{appr.ApplicantName}</div>
                        {appr.Type === 'LEAVE' && targetUser && (
                          <div className="text-[11px] text-slate-500 font-medium mt-0.5 inline-flex items-center gap-1 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                            <span>잔여 <strong className={`font-extrabold ${remL < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>{remL.toFixed(1)}일</strong></span>
                            <span className="text-slate-400 font-normal">(총 {totalL}/사용 {usedL})</span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex flex-col gap-1.5">
                          <span className={`px-2.5 py-1 rounded-lg text-xs font-bold ${
                            appr.Type === 'LEAVE' ? 'bg-indigo-50 text-indigo-700 border border-indigo-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          }`}>
                            {appr.Type === 'LEAVE' ? '휴가/연차' : '경비청구'}
                          </span>
                          {appr.Status === 'CANCEL_REQUESTED' && (
                            <span className="px-2 py-0.5 rounded bg-rose-100 text-rose-800 border border-rose-300 text-[10px] font-bold text-center">
                              취소 요청
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-slate-800 max-w-xs">
                        <span className="font-medium">{appr.Details}</span>
                        {appr.Type === 'EXPENSE' && appr.CardCompany && (
                          <div className="text-xs text-indigo-600 mt-1 font-semibold">
                            결제 정보: {appr.CardCompany} ({appr.CardNumber})
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {appr.ApprovalStages && appr.ApprovalStages.length > 0 ? (
                          <div className="flex items-center gap-1 text-xs">
                            {appr.ApprovalStages.map((st, idx) => (
                              <span key={idx} className="flex items-center gap-1">
                                <span className={`px-2 py-0.5 rounded-md text-[11px] font-bold ${
                                  st.Status === 'APPROVED' ? 'bg-emerald-100 text-emerald-800' :
                                  st.Status === 'PENDING' ? 'bg-amber-100 text-amber-800 ring-1 ring-amber-400 font-extrabold' :
                                  'bg-slate-100 text-slate-400'
                                }`}>
                                  {st.Status === 'APPROVED' ? '✓ ' : st.Status === 'PENDING' ? '⏳ ' : ''}
                                  {st.Step}차 {st.ApproverName}
                                </span>
                                {idx < appr.ApprovalStages.length - 1 && <span className="text-slate-300">➔</span>}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">1단계 결재</span>
                        )}
                      </td>
                      <td className="px-4 py-4 font-mono font-bold text-slate-900 whitespace-nowrap">
                        {appr.Amount > 0 ? `₩ ${appr.Amount.toLocaleString()}` : '-'}
                      </td>
                      <td className="px-4 py-4 text-slate-500 whitespace-nowrap">{new Date(appr.CreatedAt).toLocaleDateString()}</td>
                      <td className="px-4 py-4 text-xs font-semibold whitespace-nowrap">
                        {appr.Type === 'EXPENSE' ? (
                          (appr.ReceiptS3Url || appr.ReceiptUrl || appr.ProofUrl) ? (
                            <a href={appr.ReceiptS3Url || appr.ReceiptUrl || appr.ProofUrl} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline font-bold inline-flex items-center gap-1">
                              <span>영수증 보기</span>
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : '-'
                        ) : appr.ProofUrl ? (
                          <a href={appr.ProofUrl || appr.ReceiptS3Url || appr.ReceiptUrl} target="_blank" rel="noreferrer" className="text-emerald-600 hover:underline font-bold inline-flex items-center gap-1">
                            <span>증빙 보기</span>
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : (
                          <span className="text-slate-400 font-normal">증빙 없음</span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-right whitespace-nowrap">
                        {/* Perfectly Aligned Desktop Action Buttons with Detail Modal */}
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => setSelectedDetailApproval(appr)}
                            className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 rounded-xl text-xs font-bold transition-all cursor-pointer inline-flex items-center gap-1 shadow-2xs"
                          >
                            <span>상세보기</span>
                          </button>
                          {isCurrentApprover(appr) ? (
                            <>
                              <button
                                onClick={() => handleApprovalAction(appr.ApprovalID, 'APPROVED')}
                                className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all cursor-pointer inline-flex items-center gap-1 shadow-2xs"
                              >
                                <Check className="w-3.5 h-3.5" />
                                <span>{appr.Status === 'CANCEL_REQUESTED' ? '취소 승인' : '승인'}</span>
                              </button>
                              <button
                                onClick={() => { setRejectingApprId(appr.ApprovalID); setRejectReason(''); }}
                                className="px-3.5 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-bold transition-all cursor-pointer inline-flex items-center gap-1 shadow-2xs"
                              >
                                <X className="w-3.5 h-3.5" />
                                <span>{appr.Status === 'CANCEL_REQUESTED' ? '취소 반려' : '반려'}</span>
                              </button>
                            </>
                          ) : (
                            <span className="text-xs text-slate-400 bg-slate-50 border border-slate-200 px-2.5 py-1.5 rounded-xl font-bold flex items-center gap-1">
                              <AlertCircle className="w-3.5 h-3.5 text-slate-400" />
                              <span>{getActiveStage(appr)?.ApproverName} 순서</span>
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 2. Employee Balances List Tab */}
      {activeSubTab === 'balances' && (
        <div className="bg-white border border-slate-200 rounded-2xl p-4 md:p-6 shadow-xs space-y-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h3 className="text-base md:text-lg font-bold text-slate-900">전체 직원 연차 현황표</h3>
              <p className="text-xs md:text-sm text-slate-500 mt-1">임직원 전체의 연차 보유 현황입니다. 필요 시 관리자 권한으로 잔여 연차를 조정(강제 차감)할 수 있습니다.</p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <button
                onClick={() => setFilterNegative(!filterNegative)}
                className={`px-4 py-2.5 border rounded-xl text-xs md:text-sm font-bold flex items-center justify-center gap-1.5 transition-all cursor-pointer ${
                  filterNegative
                    ? 'bg-rose-50 border-rose-300 text-rose-700 shadow-2xs'
                    : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
                }`}
              >
                <AlertCircle className="w-4 h-4" />
                마이너스 잔여자만 필터링
              </button>

              <button
                onClick={handleReconcileFromAuditLogs}
                className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs md:text-sm font-bold shadow-sm flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
                title="감사로그의 직권 차감 내역을 전체 직원 연차 현황표에 즉시 일괄 소급 반영합니다."
              >
                <RefreshCw className="w-4 h-4" />
                감사로그 기반 연차 일괄 동기화
              </button>

              <button
                onClick={() => setIsDeducting(true)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 px-4 rounded-xl shadow-sm text-xs md:text-sm flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
              >
                <MinusCircle className="w-4 h-4" />
                직권 연차 차감 실행
              </button>
            </div>
          </div>

          {/* Mobile Card view for Balances */}
          <div className="block md:hidden space-y-3">
            {filteredUsers.map(user => (
              <div key={user.UserID} className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-2">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-base text-slate-900">{user.Name}</span>
                  <span className="text-xs text-slate-500">{user.Department} • {user.JobTitle}</span>
                </div>
                <div className="text-xs text-slate-500">{user.Email} (입사: {user.JoinDate})</div>
                <div className="flex justify-between items-center pt-2 border-t border-slate-200 text-xs">
                  <span>총 {user.TotalLeave}일 / 사용 {user.UsedLeave}일</span>
                  <span className={`font-bold px-2 py-0.5 rounded ${user.RemainingLeave < 0 ? 'bg-rose-100 text-rose-800' : 'bg-emerald-100 text-emerald-800'}`}>
                    잔여: {user.RemainingLeave}일
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table view for Balances */}
          <div className="hidden md:block overflow-x-auto border border-slate-200 rounded-xl">
            <table className="w-full text-left text-sm text-slate-800">
              <thead className="bg-slate-50 text-xs font-bold text-slate-500 uppercase tracking-wider">
                <tr>
                  <th className="px-5 py-3.5">이름</th>
                  <th className="px-4 py-3.5">메일 ID</th>
                  <th className="px-4 py-3.5">부서 / 직급</th>
                  <th className="px-4 py-3.5">입사일</th>
                  <th className="px-4 py-3.5">보유 연차</th>
                  <th className="px-4 py-3.5">사용 연차</th>
                  <th className="px-4 py-3.5">잔여 연차</th>
                  <th className="px-5 py-3.5">연차 상태 경고</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredUsers.map(user => (
                  <tr key={user.UserID} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-3.5 font-bold text-slate-900 whitespace-nowrap">{user.Name}</td>
                    <td className="px-4 py-3.5 font-mono text-xs text-slate-600 whitespace-nowrap">{user.Email}</td>
                    <td className="px-4 py-3.5 text-slate-800 whitespace-nowrap">{user.Department} / {user.JobTitle}</td>
                    <td className="px-4 py-3.5 text-slate-500 whitespace-nowrap">{user.JoinDate}</td>
                    <td className="px-4 py-3.5 text-indigo-700 font-bold whitespace-nowrap">{user.TotalLeave}일</td>
                    <td className="px-4 py-3.5 text-slate-600 whitespace-nowrap">{user.UsedLeave}일</td>
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <span className={`font-bold ${user.RemainingLeave < 0 ? 'text-rose-600' : 'text-slate-900'}`}>
                        {user.RemainingLeave}일
                      </span>
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap">
                      {user.RemainingLeave < 0 ? (
                        <span className="inline-flex items-center gap-1 text-xs text-rose-700 font-bold bg-rose-50 border border-rose-200 px-2.5 py-1 rounded-full">
                          <AlertCircle className="w-3.5 h-3.5" />
                          한도 초과 경고
                        </span>
                      ) : (
                        <span className="text-slate-400 text-xs">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 3. Admin Force Deduct Audit Log Tab */}
      {activeSubTab === 'audit' && (
        <div className="bg-white border border-slate-200 rounded-2xl p-4 md:p-6 shadow-xs space-y-6">
          <div>
            <h3 className="text-base md:text-lg font-bold text-slate-900">관리자 직권 차감 이력 (Audit Logs)</h3>
            <p className="text-xs md:text-sm text-slate-500 mt-1">인사 관리자가 강제로 직원의 연차를 차감 혹은 가산(`ADMIN_FORCE_DEDUCT`) 조치한 전체 상세 이력입니다.</p>
          </div>

          <div className="overflow-x-auto border border-slate-200 rounded-xl">
            <table className="w-full text-left text-sm text-slate-800">
              <thead className="bg-slate-50 text-xs font-bold text-slate-500 uppercase tracking-wider">
                <tr>
                  <th className="px-5 py-3.5">일시</th>
                  <th className="px-4 py-3.5">대상 직원</th>
                  <th className="px-4 py-3.5">차감/가산 일수</th>
                  <th className="px-5 py-3.5">차감 사유 (필수)</th>
                  <th className="px-4 py-3.5">처리자 (Admin)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {forceDeductLogs.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-12 text-slate-400">직권 연차 차감 이력이 없습니다.</td>
                  </tr>
                ) : (
                  forceDeductLogs.map(log => {
                    const targetUser = users.find(u => u.UserID === log.UserID) || { Name: log.UserID };
                    return (
                      <tr key={log.HistoryID} className="hover:bg-slate-50 transition-colors">
                        <td className="px-5 py-3.5 text-xs text-slate-500 font-mono whitespace-nowrap">
                          {new Date(log.Timestamp).toLocaleString()}
                        </td>
                        <td className="px-4 py-3.5 font-bold text-slate-900 whitespace-nowrap">{targetUser.Name}</td>
                        <td className="px-4 py-3.5 font-bold text-slate-900 whitespace-nowrap">
                          {log.Days > 0 ? `-${log.Days}일` : `+${Math.abs(log.Days)}일`}
                        </td>
                        <td className="px-5 py-3.5 text-slate-700 font-medium">{log.Reason}</td>
                        <td className="px-4 py-3.5 font-semibold text-indigo-700 whitespace-nowrap">{log.CreatedBy}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Force Leave Deduction Modal */}
      {isDeducting && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex justify-center items-center p-4">
          <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-md overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200 text-left">
            <div className="bg-slate-50 px-6 py-4 border-b border-slate-100 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <MinusCircle className="w-5 h-5 text-indigo-600" />
                <h2 className="text-base font-bold text-slate-900">관리자 직권 연차 조정</h2>
              </div>
              <button
                onClick={() => setIsDeducting(false)}
                className="text-slate-400 hover:text-slate-700 cursor-pointer"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleDeductSubmit} className="p-6 space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-600 uppercase">대상 직원 선택</label>
                <select
                  value={deductUserId}
                  onChange={e => setDeductUserId(e.target.value)}
                  className="w-full px-3.5 py-3 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-indigo-600 text-slate-900"
                  required
                >
                  <option value="">조정할 직원을 선택하세요</option>
                  {users.map(u => (
                    <option key={u.UserID} value={u.UserID}>
                      {u.Name} ({u.Department} • 잔여: {u.RemainingLeave}일)
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-600 uppercase">조정할 연차 일수</label>
                <input
                  type="number"
                  step="0.5"
                  placeholder="예: 2 (2일 차감) 또는 -1 (1일 가산)"
                  value={deductDays}
                  onChange={e => setDeductDays(e.target.value)}
                  className="w-full px-3.5 py-3 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-indigo-600 text-slate-900"
                  required
                />
                <span className="text-xs text-slate-500 leading-normal block">
                  양수(+)값을 입력하면 차감, 음수(-)값을 입력하면 가산됩니다.
                </span>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-600 uppercase">연차 직권 조정 사유 (필수)</label>
                <textarea
                  rows="3"
                  placeholder="직권 조정 사유를 상세하게 입력해 주세요."
                  value={deductReason}
                  onChange={e => setDeductReason(e.target.value)}
                  className="w-full px-3.5 py-3 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-indigo-600 text-slate-900 resize-none"
                  required
                />
              </div>

              <div className="bg-amber-50 border border-amber-200 p-3.5 rounded-xl text-xs text-amber-800 leading-relaxed">
                <strong>주의:</strong> 강제 차감 내역은 감사 이력 로그(`ADMIN_FORCE_DEDUCT`)에 영구적으로 귀속되어 기록됩니다.
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsDeducting(false)}
                  className="px-4 py-2.5 rounded-xl text-sm font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 cursor-pointer"
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 rounded-xl text-sm font-bold bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm transition-all cursor-pointer"
                >
                  연차 강제 조정 실행
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reject Reason Modal */}
      {rejectingApprId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4">
          <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-md shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 text-left">
            <div className="flex justify-between items-center p-5 border-b border-slate-100 bg-slate-50">
              <div className="flex items-center gap-2 text-rose-600 font-bold">
                <ShieldAlert className="w-5 h-5" />
                <h2 className="text-base font-bold text-slate-900">결재 반려 사유 입력</h2>
              </div>
              <button
                onClick={() => setRejectingApprId(null)}
                className="text-slate-400 hover:text-slate-700 cursor-pointer"
              >
                ✕
              </button>
            </div>

            <form onSubmit={(e) => {
              e.preventDefault();
              if (!rejectReason.trim()) {
                alert('반려 사유를 입력해 주세요.');
                return;
              }
              handleApprovalAction(rejectingApprId, 'REJECTED', rejectReason.trim());
              setRejectingApprId(null);
              setRejectReason('');
              alert('반려 처리가 완료되었습니다.');
            }} className="p-6 space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-600 uppercase">반려 사유 (신청인에게 노출됨)</label>
                <textarea
                  rows="4"
                  placeholder="반려 사유를 입력해 주세요. (예: 업무 진행 일정상 연차 변경 요청)"
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  className="w-full px-3.5 py-3 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-indigo-600 text-slate-900 resize-none"
                  required
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setRejectingApprId(null)}
                  className="px-4 py-2.5 rounded-xl text-sm font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 cursor-pointer"
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 rounded-xl text-sm font-bold bg-rose-600 hover:bg-rose-700 text-white shadow-sm transition-all cursor-pointer"
                >
                  반려 완료
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 4. Leave History Search Tab (Requirement 2) */}
      {activeSubTab === 'history' && (() => {
        const paginatedHistoryList = searchedHistoryList.slice((historyPage - 1) * ITEMS_PER_PAGE, historyPage * ITEMS_PER_PAGE);

        return (
          <div className="bg-white border border-slate-200 rounded-2xl p-4 md:p-6 shadow-xs space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="text-base md:text-lg font-bold text-slate-900 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-indigo-600" />
                  <span>전체 연차 신청 및 변동 이력</span>
                </h3>
                <p className="text-xs md:text-sm text-slate-500 mt-1">임직원들의 연차 생성, 소진, 결재 완료된 전체 이력을 기간별로 검색하고 확인합니다.</p>
              </div>
            </div>

            {/* Date Range Selector & Quick Presets for Leave */}
            <div className="bg-slate-50 border border-slate-200 p-4 rounded-2xl space-y-3 text-left">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-indigo-600" />
                  <span className="text-xs font-bold text-slate-800">조회 기간 설정</span>
                  <span className="text-[11px] font-mono text-slate-500 bg-white px-2.5 py-0.5 rounded-full border border-slate-200">
                    {historyStartDate ? `${historyStartDate} ~ ${historyEndDate || '현재'}` : '전체 기간'}
                  </span>
                </div>

                {/* Quick Preset Buttons */}
                <div className="flex flex-wrap items-center gap-1.5">
                  {[
                    { id: 'THIS_MONTH', label: '이번 달 (당월)' },
                    { id: 'LAST_MONTH', label: '지난 달' },
                    { id: 'LAST_3_MONTHS', label: '최근 3개월' },
                    { id: 'THIS_YEAR', label: '올해 (연간)' },
                    { id: 'ALL', label: '전체 기간' }
                  ].map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => handleHistoryDatePresetClick(p.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                        historyDatePreset === p.id
                          ? 'bg-indigo-600 text-white shadow-2xs'
                          : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Date Pickers */}
              <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-slate-200/60">
                <span className="text-[11px] font-bold text-slate-500">직접 지정:</span>
                <input
                  type="date"
                  value={historyStartDate}
                  onChange={e => {
                    setHistoryStartDate(e.target.value);
                    setHistoryDatePreset('CUSTOM');
                    setHistoryPage(1);
                  }}
                  className="px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold text-slate-800 focus:outline-none focus:border-indigo-600"
                />
                <span className="text-slate-400 font-bold">~</span>
                <input
                  type="date"
                  value={historyEndDate}
                  onChange={e => {
                    setHistoryEndDate(e.target.value);
                    setHistoryDatePreset('CUSTOM');
                    setHistoryPage(1);
                  }}
                  className="px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold text-slate-800 focus:outline-none focus:border-indigo-600"
                />
              </div>
            </div>

            {/* Search Filters */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-slate-50/50 p-4 rounded-2xl border border-slate-200">
              <div className="space-y-1">
                <label className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider block">이름 또는 부서 검색</label>
                <input
                  type="text"
                  placeholder="사원명 또는 부서 입력..."
                  value={historySearchTerm}
                  onChange={e => {
                    setHistorySearchTerm(e.target.value);
                    setHistoryPage(1);
                  }}
                  className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-600 font-medium"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider block">변동 유형 필터</label>
                <select
                  value={historyTypeFilter}
                  onChange={e => {
                    setHistoryTypeFilter(e.target.value);
                    setHistoryPage(1);
                  }}
                  className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-600 font-bold"
                >
                  <option value="ALL">전체 보기 (ALL)</option>
                  <option value="LEAVE_APPROVED">최종 결재 완료 (연차 소진)</option>
                  <option value="LEAVE_REJECTED">반려됨 (연차 취소)</option>
                  <option value="LEAVE_PENDING">결재 진행/대기 중</option>
                  <option value="ANNUAL_GRANT">정기 부여 (연차 지급)</option>
                  <option value="ADMIN_FORCE_DEDUCT">관리자 직권 차감/가산</option>
                </select>
              </div>
              
              <div className="flex items-end justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setHistorySearchTerm('');
                    setHistoryTypeFilter('ALL');
                    setHistoryDatePreset('ALL');
                    setHistoryStartDate('');
                    setHistoryEndDate('');
                    setHistoryPage(1);
                  }}
                  className="px-4 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold text-xs rounded-xl cursor-pointer transition-all w-full sm:w-auto"
                >
                  필터 초기화
                </button>
              </div>
            </div>

            {/* Leave History Table */}
            <div className="overflow-x-auto border border-slate-200 rounded-2xl">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 font-bold text-slate-700 text-xs">
                  <tr>
                    <th className="px-4 py-3 text-left">일시</th>
                    <th className="px-4 py-3 text-left">사원명</th>
                    <th className="px-4 py-3 text-left">소속 / 직급</th>
                    <th className="px-4 py-3 text-left">변동 구분</th>
                    <th className="px-4 py-3 text-left">변동 일수</th>
                    <th className="px-4 py-3 text-left">세부 내용 및 결재선</th>
                    <th className="px-4 py-3 text-left">처리자</th>
                    <th className="px-4 py-3 text-left">액션</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 text-xs">
                  {searchedHistoryList.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-10 text-slate-400 font-medium">
                        검색 조건에 부합하는 연차 변동 이력이 없습니다.
                      </td>
                    </tr>
                  ) : (
                    paginatedHistoryList.map(item => {
                      const targetUser = users.find(u => u.UserID === item.UserID) || {};
                      let typeBadge = '';
                      let typeLabel = '';
                      if (item.ChangeType === 'ANNUAL_GRANT') {
                        typeBadge = 'bg-emerald-50 text-emerald-700 border-emerald-200';
                        typeLabel = '연차 부여';
                      } else if (item.ChangeType === 'LEAVE_APPROVED') {
                        typeBadge = 'bg-indigo-50 text-indigo-700 border-indigo-200';
                        typeLabel = '결재 승인';
                      } else if (item.ChangeType === 'LEAVE_REJECTED') {
                        typeBadge = 'bg-rose-50 text-rose-700 border-rose-200';
                        typeLabel = '결재 반려';
                      } else if (item.ChangeType === 'LEAVE_PENDING') {
                        typeBadge = 'bg-amber-50 text-amber-700 border-amber-200';
                        typeLabel = '결재 대기';
                      } else if (item.ChangeType === 'ADMIN_FORCE_DEDUCT') {
                        typeBadge = 'bg-amber-50 text-amber-700 border-amber-200';
                        typeLabel = '직권 변동';
                      } else {
                        typeBadge = 'bg-slate-50 text-slate-700 border-slate-200';
                        typeLabel = item.ChangeType;
                      }

                      const displayDays = item.ChangeType === 'LEAVE_REJECTED' ? '0' : (item.Days > 0 ? `+${item.Days}` : item.Days);
                      const daysSuffix = item.ChangeType === 'LEAVE_PENDING' ? ' (대기)' : item.ChangeType === 'LEAVE_REJECTED' ? ' (반려)' : '';

                      return (
                        <tr key={item.HistoryID} className="hover:bg-slate-50/50 transition-colors">
                          <td className="px-4 py-3 text-slate-400 font-mono">
                            {new Date(item.Timestamp).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 font-bold text-slate-900">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span>{targetUser.Name || '알 수 없음'}</span>
                              <span className="text-[10px] font-extrabold px-1.5 py-0.2 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200" title="실제 잔여 연차">
                                잔여 {targetUser.RemainingLeave ?? 15}일
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-500">
                            {targetUser.Department || '-'} / {targetUser.JobTitle || targetUser.Position || '-'}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded-md font-bold border text-[10px] ${typeBadge}`}>
                              {typeLabel}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-black text-slate-800">
                            {displayDays}{daysSuffix} 일
                          </td>
                          <td className="px-4 py-3 text-slate-600 font-medium">
                            {item.Reason}
                          </td>
                          <td className="px-4 py-3 text-slate-500 font-bold">
                            {item.CreatedBy}
                          </td>
                          <td className="px-4 py-3">
                            {item.isApproval ? (
                              <button
                                onClick={() => setSelectedDetailApproval(item.approvalItem)}
                                className="px-2.5 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold rounded-lg border border-indigo-100 transition-colors cursor-pointer select-none"
                              >
                                상세 보기
                              </button>
                            ) : (
                              <span className="text-slate-400 font-bold">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination for Leave History */}
            {searchedHistoryList.length > ITEMS_PER_PAGE && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-slate-200 text-xs">
                <span className="text-slate-500 font-medium">
                  전체 <strong className="text-slate-900 font-bold">{searchedHistoryList.length.toLocaleString()}</strong>건 중{' '}
                  <strong className="text-indigo-600 font-bold">{(historyPage - 1) * ITEMS_PER_PAGE + 1}-{Math.min(historyPage * ITEMS_PER_PAGE, searchedHistoryList.length)}</strong>건 표시 (페이지 {historyPage} / {Math.ceil(searchedHistoryList.length / ITEMS_PER_PAGE)})
                </span>

                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setHistoryPage(prev => Math.max(1, prev - 1))}
                    disabled={historyPage === 1}
                    className="px-2.5 py-1.5 rounded-lg border border-slate-300 bg-white font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-all"
                  >
                    ‹ 이전
                  </button>

                  {Array.from({ length: Math.ceil(searchedHistoryList.length / ITEMS_PER_PAGE) }, (_, i) => i + 1).map(p => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setHistoryPage(p)}
                      className={`w-7 h-7 rounded-lg font-bold text-xs transition-all cursor-pointer ${
                        historyPage === p
                          ? 'bg-indigo-600 text-white shadow-xs'
                          : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {p}
                    </button>
                  ))}

                  <button
                    type="button"
                    onClick={() => setHistoryPage(prev => Math.min(Math.ceil(searchedHistoryList.length / ITEMS_PER_PAGE), prev + 1))}
                    disabled={historyPage === Math.ceil(searchedHistoryList.length / ITEMS_PER_PAGE)}
                    className="px-2.5 py-1.5 rounded-lg border border-slate-300 bg-white font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-all"
                  >
                    다음 ›
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* 5. Expense History Search Tab */}
      {activeSubTab === 'expenseHistory' && (() => {
        const paginatedExpenseList = searchedExpenseList.slice((expensePage - 1) * ITEMS_PER_PAGE, expensePage * ITEMS_PER_PAGE);

        return (
          <div className="bg-white border border-slate-200 rounded-2xl p-4 md:p-6 shadow-xs space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="text-base md:text-lg font-bold text-slate-900 flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-emerald-600" />
                  <span>전체 경비 결재요청 및 정산 이력</span>
                </h3>
                <p className="text-xs md:text-sm text-slate-500 mt-1">
                  전사 임직원의 법인카드 및 개인 경비 지출 청구, 승인/반려 내역, 영수증 원본을 기간별로 조회합니다.
                </p>
              </div>
              <button
                type="button"
                onClick={exportExpenseToCsv}
                className="flex items-center gap-1.5 px-4 py-2.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border border-emerald-200 font-bold text-xs rounded-xl cursor-pointer transition-all shadow-2xs self-start sm:self-auto"
              >
                <Download className="w-4 h-4 text-emerald-600" />
                <span>CSV 엑셀 다운로드</span>
              </button>
            </div>

            {/* Date Range Selector & Quick Presets */}
            <div className="bg-slate-50 border border-slate-200 p-4 rounded-2xl space-y-3 text-left">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-indigo-600" />
                  <span className="text-xs font-bold text-slate-800">조회 기간 설정</span>
                  <span className="text-[11px] font-mono text-slate-500 bg-white px-2.5 py-0.5 rounded-full border border-slate-200">
                    {expenseStartDate ? `${expenseStartDate} ~ ${expenseEndDate || '현재'}` : '전체 기간'}
                  </span>
                </div>

                {/* Quick Preset Buttons */}
                <div className="flex flex-wrap items-center gap-1.5">
                  {[
                    { id: 'THIS_MONTH', label: '이번 달 (당월)' },
                    { id: 'LAST_MONTH', label: '지난 달' },
                    { id: 'LAST_3_MONTHS', label: '최근 3개월' },
                    { id: 'THIS_YEAR', label: '올해 (연간)' },
                    { id: 'ALL', label: '전체 기간' }
                  ].map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => handleDatePresetClick(p.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                        expenseDatePreset === p.id
                          ? 'bg-indigo-600 text-white shadow-2xs'
                          : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Date Pickers */}
              <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-slate-200/60">
                <span className="text-[11px] font-bold text-slate-500">직접 지정:</span>
                <input
                  type="date"
                  value={expenseStartDate}
                  onChange={e => {
                    setExpenseStartDate(e.target.value);
                    setExpenseDatePreset('CUSTOM');
                    setExpensePage(1);
                  }}
                  className="px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold text-slate-800 focus:outline-none focus:border-indigo-600"
                />
                <span className="text-slate-400 font-bold">~</span>
                <input
                  type="date"
                  value={expenseEndDate}
                  onChange={e => {
                    setExpenseEndDate(e.target.value);
                    setExpenseDatePreset('CUSTOM');
                    setExpensePage(1);
                  }}
                  className="px-3 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-semibold text-slate-800 focus:outline-none focus:border-indigo-600"
                />
              </div>
            </div>

            {/* Total Expense Claims Metric Summary Box */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-slate-50 border border-slate-200 p-4 rounded-2xl">
                <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider block">총 신청 금액 ({totalExpenseClaimsCount}건)</span>
                <div className="mt-1 flex items-baseline gap-1">
                  <span className="text-2xl font-black text-slate-900">{totalExpenseClaimsCount}건</span>
                </div>
                <span className="text-xs font-mono font-bold text-slate-700 block mt-1">₩ {totalExpenseClaimsAmount.toLocaleString()}</span>
              </div>

              <div className="bg-emerald-50/60 border border-emerald-200/80 p-4 rounded-2xl">
                <span className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider block">승인 완료 금액</span>
                <div className="mt-1 flex items-baseline gap-1">
                  <span className="text-2xl font-black text-emerald-700">{allExpenseClaims.filter(a => a.Status === 'APPROVED').length}건</span>
                </div>
                <span className="text-xs font-mono font-black text-emerald-700 block mt-1">₩ {approvedExpenseClaimsAmount.toLocaleString()}</span>
              </div>

              <div className="bg-amber-50/60 border border-amber-200/80 p-4 rounded-2xl">
                <span className="text-[11px] font-bold text-amber-700 uppercase tracking-wider block">결재 대기 중</span>
                <div className="mt-1 flex items-baseline gap-1">
                  <span className="text-2xl font-black text-amber-700">{allExpenseClaims.filter(a => a.Status === 'PENDING').length}건</span>
                </div>
                <span className="text-xs font-mono font-black text-amber-700 block mt-1">₩ {pendingExpenseClaimsAmount.toLocaleString()}</span>
              </div>

              <div className="bg-rose-50/60 border border-rose-200/80 p-4 rounded-2xl">
                <span className="text-[11px] font-bold text-rose-700 uppercase tracking-wider block">반려 금액</span>
                <div className="mt-1 flex items-baseline gap-1">
                  <span className="text-2xl font-black text-rose-700">{allExpenseClaims.filter(a => a.Status === 'REJECTED').length}건</span>
                </div>
                <span className="text-xs font-mono font-bold text-rose-700 block mt-1">₩ {rejectedExpenseClaimsAmount.toLocaleString()}</span>
              </div>
            </div>

            {/* Search & Multi-Filters */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 bg-slate-50/60 p-4 rounded-2xl border border-slate-200">
              <div className="space-y-1 sm:col-span-2">
                <label className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider block">사원명 / 부서 / 사용처 검색</label>
                <input
                  type="text"
                  placeholder="사원명, 부서명, 가맹점 상호 검색..."
                  value={expenseSearchTerm}
                  onChange={e => {
                    setExpenseSearchTerm(e.target.value);
                    setExpensePage(1);
                  }}
                  className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-600 font-medium"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider block">결재 상태</label>
                <select
                  value={expenseStatusFilter}
                  onChange={e => {
                    setExpenseStatusFilter(e.target.value);
                    setExpensePage(1);
                  }}
                  className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-600 font-bold"
                >
                  <option value="ALL">전체 상태 (ALL)</option>
                  <option value="PENDING">결재 대기 중</option>
                  <option value="APPROVED">승인 완료</option>
                  <option value="REJECTED">반려</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider block">경비 분류</label>
                <select
                  value={expenseCategoryFilter}
                  onChange={e => {
                    setExpenseCategoryFilter(e.target.value);
                    setExpensePage(1);
                  }}
                  className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-600 font-bold"
                >
                  <option value="ALL">전체 분류 (ALL)</option>
                  <option value="식대">식대</option>
                  <option value="교통비">교통비</option>
                  <option value="소모품">소모품</option>
                  <option value="회의비">회의비</option>
                  <option value="기타">기타</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider block">결제 카드</label>
                <select
                  value={expenseCardFilter}
                  onChange={e => {
                    setExpenseCardFilter(e.target.value);
                    setExpensePage(1);
                  }}
                  className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:outline-none focus:border-indigo-600 font-bold"
                >
                  <option value="ALL">전체 카드사 (ALL)</option>
                  <option value="국민카드">국민카드</option>
                  <option value="신한카드">신한카드</option>
                  <option value="현대카드">현대카드</option>
                  <option value="삼성카드">삼성카드</option>
                  <option value="비씨카드">비씨카드</option>
                  <option value="롯데카드">롯데카드</option>
                </select>
              </div>
            </div>

            {/* Expense History Table */}
            <div className="overflow-x-auto border border-slate-200 rounded-2xl">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 font-bold text-slate-700 text-xs">
                  <tr>
                    <th className="px-4 py-3 text-left">신청일시</th>
                    <th className="px-4 py-3 text-left">사원명</th>
                    <th className="px-4 py-3 text-left">소속 / 직급</th>
                    <th className="px-4 py-3 text-left">분류</th>
                    <th className="px-4 py-3 text-left">사용처 / 가맹점</th>
                    <th className="px-4 py-3 text-left">청구 금액</th>
                    <th className="px-4 py-3 text-left">결제 카드</th>
                    <th className="px-4 py-3 text-left">결재 상태</th>
                    <th className="px-4 py-3 text-left">영수증</th>
                    <th className="px-4 py-3 text-left">상세</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 text-xs">
                  {searchedExpenseList.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="text-center py-10 text-slate-400 font-medium">
                        검색 조건에 부합하는 경비 결재 및 청구 이력이 없습니다.
                      </td>
                    </tr>
                  ) : (
                    paginatedExpenseList.map(item => {
                      const targetUser = users.find(u => u.UserID === item.ApplicantID) || {};
                      const category = item.Details?.match(/\[(.*?)\]/)?.[1] || '경비';
                      const cleanDetails = item.Details?.replace(/\[(.*?)\]/, '').trim() || item.Details;
                      const receiptUrl = item.ReceiptS3Url || item.ProofUrl;

                      return (
                        <tr key={item.ApprovalID} className="hover:bg-slate-50/50 transition-colors">
                          <td className="px-4 py-3 text-slate-400 font-mono">
                            {new Date(item.CreatedAt).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 font-bold text-slate-900">
                            {item.ApplicantName || targetUser.Name || '알 수 없음'}
                          </td>
                          <td className="px-4 py-3 text-slate-500">
                            {item.ApplicantDept || targetUser.Department || '-'} / {targetUser.JobTitle || '-'}
                          </td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-700 font-bold rounded-md border border-slate-200">
                              {category}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-semibold text-slate-800">
                            {cleanDetails}
                          </td>
                          <td className="px-4 py-3 font-mono font-black text-slate-900">
                            ₩ {Number(item.Amount || 0).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-slate-500">
                            {item.CardCompany ? (
                              <span className="font-medium text-slate-700">{item.CardCompany} {item.CardNumber ? `(${item.CardNumber.slice(-4)})` : ''}</span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold border ${
                              item.Status === 'APPROVED' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                              item.Status === 'REJECTED' ? 'bg-rose-50 text-rose-700 border-rose-200' :
                              'bg-amber-50 text-amber-700 border-amber-200'
                            }`}>
                              {item.Status === 'APPROVED' ? '승인 완료' : item.Status === 'REJECTED' ? '반려됨' : '결재 대기'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {receiptUrl ? (
                              <a
                                href={receiptUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-bold rounded-lg border border-emerald-200 transition-colors"
                              >
                                <ExternalLink className="w-3 h-3" />
                                <span>영수증</span>
                              </a>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => setSelectedDetailApproval(item)}
                              className="px-2.5 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold rounded-lg border border-indigo-100 transition-colors cursor-pointer select-none"
                            >
                              상세 보기
                            </button>
                          </td>
                        </tr>
                      );
                    })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination for Admin Expense History */}
          {searchedExpenseList.length > ITEMS_PER_PAGE && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-slate-200 text-xs">
              <span className="text-slate-500 font-medium">
                전체 <strong className="text-slate-900 font-bold">{searchedExpenseList.length.toLocaleString()}</strong>건 중{' '}
                <strong className="text-indigo-600 font-bold">{(expensePage - 1) * ITEMS_PER_PAGE + 1}-{Math.min(expensePage * ITEMS_PER_PAGE, searchedExpenseList.length)}</strong>건 표시 (페이지 {expensePage} / {Math.ceil(searchedExpenseList.length / ITEMS_PER_PAGE)})
              </span>

              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setExpensePage(prev => Math.max(1, prev - 1))}
                  disabled={expensePage === 1}
                  className="px-2.5 py-1.5 rounded-lg border border-slate-300 bg-white font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-all"
                >
                  ‹ 이전
                </button>

                {Array.from({ length: Math.ceil(searchedExpenseList.length / ITEMS_PER_PAGE) }, (_, i) => i + 1).map(p => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setExpensePage(p)}
                    className={`w-7 h-7 rounded-lg font-bold text-xs transition-all cursor-pointer ${
                      expensePage === p
                        ? 'bg-indigo-600 text-white shadow-xs'
                        : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {p}
                  </button>
                ))}

                <button
                  type="button"
                  onClick={() => setExpensePage(prev => Math.min(Math.ceil(searchedExpenseList.length / ITEMS_PER_PAGE), prev + 1))}
                  disabled={expensePage === Math.ceil(searchedExpenseList.length / ITEMS_PER_PAGE)}
                  className="px-2.5 py-1.5 rounded-lg border border-slate-300 bg-white font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-all"
                >
                  다음 ›
                </button>
              </div>
            </div>
          )}
        </div>
      );
    })()}

      {/* 5. Details Modal for Combined History (Leave Applications vs Expense Claims Details) */}
      {selectedDetailApproval && (() => {
        const isExpense = selectedDetailApproval.Type === 'EXPENSE';
        const hasCategory = selectedDetailApproval.Details?.startsWith('[') && selectedDetailApproval.Details?.includes(']');
        const categoryText = hasCategory 
          ? selectedDetailApproval.Details.substring(1, selectedDetailApproval.Details.indexOf(']'))
          : (isExpense ? '기타 경비' : '일반');
        const cleanDetails = hasCategory 
          ? selectedDetailApproval.Details.substring(selectedDetailApproval.Details.indexOf(']') + 1).trim()
          : selectedDetailApproval.Details;

        return (
          <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex justify-center items-center p-4">
            <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-xl overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200 text-left">
              <div className="bg-gradient-to-r from-indigo-700 to-indigo-900 px-6 py-4 flex items-center justify-between text-white">
                <div className="flex items-center gap-2.5">
                  <span className="text-xl">{isExpense ? '💳' : '🌴'}</span>
                  <div>
                    <h2 className="text-base md:text-lg font-bold text-white">
                      {isExpense ? '경비 신청 결재 상세 조회' : '연차 신청 결재 상세 조회'}
                    </h2>
                    <span className="text-[11px] text-indigo-200 font-medium">
                      {isExpense ? 'Expense Reimbursement Claim Details' : 'Leave Application Details'}
                    </span>
                  </div>
                </div>
                <button 
                  onClick={() => setSelectedDetailApproval(null)}
                  className="text-white/80 hover:text-white text-xl font-bold cursor-pointer"
                >
                  ✕
                </button>
              </div>

              <div className="p-6 space-y-5 max-h-[75vh] overflow-y-auto">
                {/* Applicant Info */}
                <div className="grid grid-cols-2 gap-3.5 bg-slate-50 p-4 rounded-2xl border border-slate-200/60 text-xs">
                  <div>
                    <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block mb-0.5">신청 사원</span>
                    <span className="text-sm font-bold text-slate-800">{selectedDetailApproval.ApplicantName || '알 수 없음'}</span>
                  </div>
                  <div>
                    <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block mb-0.5">소속 / 직급</span>
                    <span className="text-sm font-bold text-slate-800">
                      {selectedDetailApproval.ApplicantDept || '-'} / {selectedDetailApproval.ApplicantJobTitle || '-'}
                    </span>
                  </div>
                  <div>
                    <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block mb-0.5">신청 일시</span>
                    <span className="text-xs font-semibold text-slate-600">
                      {new Date(selectedDetailApproval.CreatedAt).toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block mb-0.5">결재 상태</span>
                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-extrabold ${
                      selectedDetailApproval.Status === 'APPROVED' ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' :
                      selectedDetailApproval.Status === 'REJECTED' ? 'bg-rose-100 text-rose-800 border border-rose-200' :
                      'bg-amber-100 text-amber-800 border border-amber-200'
                    }`}>
                      {selectedDetailApproval.Status === 'APPROVED' ? '승인 완료' : selectedDetailApproval.Status === 'REJECTED' ? '반려됨' : '결재 진행 중'}
                    </span>
                  </div>
                </div>

                {/* Branch by Type: EXPENSE vs LEAVE */}
                {isExpense ? (
                  /* 1. EXPENSE Request Info */
                  <div className="space-y-3">
                    <h4 className="text-xs font-extrabold text-slate-800 uppercase tracking-wider">경비 청구 및 결제 정보</h4>
                    <div className="border border-slate-200 rounded-2xl p-4 space-y-3 bg-white">
                      <div className="flex justify-between items-center text-sm border-b border-slate-100 pb-2.5">
                        <span className="font-semibold text-slate-500 text-xs">경비 분류</span>
                        <span className="px-2.5 py-0.5 rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-200 text-xs font-bold">
                          {categoryText}
                        </span>
                      </div>
                      
                      <div className="flex justify-between items-center text-sm border-b border-slate-100 pb-2.5">
                        <span className="font-semibold text-slate-500 text-xs">결제 금액</span>
                        <span className="text-base font-black text-indigo-700 font-mono">
                          ₩ {Number(selectedDetailApproval.Amount || 0).toLocaleString()}
                        </span>
                      </div>

                      <div className="flex justify-between items-center text-sm border-b border-slate-100 pb-2.5">
                        <span className="font-semibold text-slate-500 text-xs">결제 카드 정보</span>
                        <span className="font-bold text-slate-800 flex items-center gap-1.5 text-xs">
                          <span>💳 {selectedDetailApproval.CardCompany || '카드사 정보 없음'}</span>
                          {selectedDetailApproval.CardNumber && (
                            <span className="text-xs text-slate-400 font-mono">({selectedDetailApproval.CardNumber})</span>
                          )}
                        </span>
                      </div>

                      <div className="space-y-1.5 pt-0.5">
                        <span className="text-xs font-semibold text-slate-500 block">사용처 및 상세 내역</span>
                        <p className="text-xs text-slate-800 font-bold bg-slate-50 p-3 rounded-xl border border-slate-200 whitespace-pre-wrap leading-relaxed">
                          {cleanDetails || selectedDetailApproval.Details}
                        </p>
                      </div>

                      {/* Receipt File */}
                      <div className="pt-2 flex justify-between items-center text-sm border-t border-slate-100">
                        <div className="flex flex-col">
                          <span className="font-semibold text-slate-500 text-xs">영수증 증빙</span>
                          {selectedDetailApproval.ProofFileName && (
                            <span className="text-[11px] text-slate-400 font-mono truncate max-w-[200px]">
                              {selectedDetailApproval.ProofFileName}
                            </span>
                          )}
                        </div>
                        {(selectedDetailApproval.ReceiptS3Url || selectedDetailApproval.ProofUrl) ? (
                          <a 
                            href={selectedDetailApproval.ReceiptS3Url || selectedDetailApproval.ProofUrl} 
                            target="_blank" 
                            rel="noreferrer" 
                            className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl transition-colors inline-flex items-center gap-1.5 shadow-xs cursor-pointer"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                            <span>영수증 원본 열기</span>
                          </a>
                        ) : (
                          <span className="text-slate-400 font-bold text-xs">첨부 영수증 없음</span>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (() => {
                  /* 2. LEAVE Request Info & Calculation System */
                  const applicantUser = users.find(u => u.UserID === selectedDetailApproval.ApplicantID || u.Name === selectedDetailApproval.ApplicantName) || {};
                  const applicantTotalLeave = (applicantUser.TotalLeave !== undefined && applicantUser.TotalLeave !== null && applicantUser.TotalLeave !== '') ? Number(applicantUser.TotalLeave) : 15;
                  const applicantUsedLeave = Number(applicantUser.UsedLeave || 0);
                  const applicantBaseRemaining = (applicantUser.RemainingLeave !== undefined && applicantUser.RemainingLeave !== null && applicantUser.RemainingLeave !== '') ? Number(applicantUser.RemainingLeave) : (applicantTotalLeave - applicantUsedLeave);

                  const otherPendingList = (approvals || []).filter(a => 
                    a.Type === 'LEAVE' && 
                    a.Status === 'PENDING' && 
                    a.ApprovalID !== selectedDetailApproval.ApprovalID &&
                    (a.ApplicantID === selectedDetailApproval.ApplicantID || a.ApplicantName === selectedDetailApproval.ApplicantName)
                  );
                  const otherPendingDays = otherPendingList.reduce((s, a) => s + (Number(a.LeaveDays || a.Days) || 0), 0);

                  const currentRequestDays = Number(selectedDetailApproval.LeaveDays || selectedDetailApproval.Days || 0);
                  const isPending = selectedDetailApproval.Status === 'PENDING';
                  const allPendingDays = otherPendingDays + (isPending ? currentRequestDays : 0);
                  const applicantCurrentRemaining = applicantBaseRemaining - otherPendingDays - (isPending ? currentRequestDays : 0);
                  const isApproved = selectedDetailApproval.Status === 'APPROVED';
                  const isSpecial = Boolean(selectedDetailApproval.IsSpecial);
                  const beforeBalance = selectedDetailApproval.BeforeLeaveBalance !== undefined
                    ? Number(selectedDetailApproval.BeforeLeaveBalance)
                    : (isApproved
                        ? (applicantBaseRemaining + (isSpecial ? 0 : currentRequestDays))
                        : (applicantBaseRemaining - otherPendingDays));

                  const afterBalance = selectedDetailApproval.AfterLeaveBalance !== undefined
                    ? Number(selectedDetailApproval.AfterLeaveBalance)
                    : (isApproved
                        ? applicantBaseRemaining
                        : (isSpecial ? beforeBalance : (beforeBalance - currentRequestDays)));

                  const isCancelReq = selectedDetailApproval.Status === 'CANCEL_REQUESTED' || selectedDetailApproval.Status === 'CANCELED';
                  const restoredBalance = applicantCurrentRemaining + currentRequestDays;

                  const leaveTypeDisplay = selectedDetailApproval.IsSpecial ? (
                    <span className="text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded font-bold border border-emerald-200">
                      특별휴가 ({selectedDetailApproval.SpecialType || '-'})
                    </span>
                  ) : selectedDetailApproval.LeaveType === 'HALF_AM' ? (
                    <span className="text-sky-700 bg-sky-50 px-2 py-0.5 rounded font-black border border-sky-200 inline-flex items-center gap-1">
                      <span>☀️</span> 오전반차 (09:00~13:00)
                    </span>
                  ) : selectedDetailApproval.LeaveType === 'HALF_PM' ? (
                    <span className="text-amber-700 bg-amber-50 px-2 py-0.5 rounded font-black border border-amber-200 inline-flex items-center gap-1">
                      <span>🌙</span> 오후반차 (14:00~18:00)
                    </span>
                  ) : selectedDetailApproval.LeaveType === 'HALF' ? (
                    <span className="text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded font-bold border border-indigo-200">
                      반차 (0.5일)
                    </span>
                  ) : selectedDetailApproval.LeaveType === 'QUARTER' ? (
                    <span className="text-teal-700 bg-teal-50 px-2 py-0.5 rounded font-bold border border-teal-200">
                      반반차 (0.25일)
                    </span>
                  ) : (
                    <span className="text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded font-bold border border-indigo-200">
                      연차
                    </span>
                  );

                  return (
                    <div className="space-y-4">
                      {/* 1. Applicant 4-Tier Leave Overview */}
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <h4 className="text-xs font-extrabold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                            <span>👤</span> 신청자 연차 현황 ({selectedDetailApproval.ApplicantName})
                          </h4>
                          <span className="text-[10px] text-slate-400 font-bold">2026 회계연도 기준</span>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 text-xs text-left">
                          <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-200">
                            <span className="text-[10px] font-bold text-slate-400 uppercase block mb-0.5">총 부여 연차</span>
                            <span className="text-sm font-black text-slate-900">{applicantTotalLeave}일</span>
                          </div>
                          <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-200">
                            <span className="text-[10px] font-bold text-slate-400 uppercase block mb-0.5">사용 완료</span>
                            <span className="text-sm font-black text-slate-900">{applicantUsedLeave}일</span>
                          </div>
                          <div className="bg-amber-50/70 p-2.5 rounded-xl border border-amber-200/80">
                            <span className="text-[10px] font-bold text-amber-600 uppercase block mb-0.5">결재 대기 중</span>
                            <span className="text-sm font-black text-amber-600">{allPendingDays}일</span>
                          </div>
                          <div className="bg-emerald-50/80 p-2.5 rounded-xl border border-emerald-200/90">
                            <span className="text-[10px] font-bold text-emerald-700 uppercase block mb-0.5">현재 실질 잔여</span>
                            <span className="text-sm font-black text-emerald-600">{applicantCurrentRemaining.toFixed(1)}일</span>
                          </div>
                        </div>
                      </div>

                      {/* 2. Real-time Calculation Formula Banner (Approval / Cancellation) */}
                      {isCancelReq ? (
                        <div className="p-3.5 bg-gradient-to-r from-emerald-50 via-teal-50 to-sky-50 rounded-2xl border border-emerald-200 space-y-2">
                          <div className="flex justify-between items-center text-xs">
                            <span className="font-extrabold text-emerald-900 flex items-center gap-1.5">
                              <span>🔄</span> 연차 취소 승인 시 복구(환원) 계산
                            </span>
                            <span className="text-[11px] font-bold text-emerald-700 bg-white px-2 py-0.5 rounded-md border border-emerald-200 shadow-2xs">
                              +{currentRequestDays}일 연차 환원
                            </span>
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-xs bg-white p-2.5 rounded-xl border border-emerald-100 shadow-2xs text-center">
                            <div>
                              <span className="text-[10px] text-slate-400 font-bold block mb-0.5">현재 잔여</span>
                              <span className="font-black text-sm text-slate-900">{applicantCurrentRemaining.toFixed(1)}일</span>
                            </div>
                            <div className="border-x border-slate-100">
                              <span className="text-[10px] text-emerald-600 font-bold block mb-0.5">취소 환원 일수</span>
                              <span className="font-black text-sm text-emerald-600">+{currentRequestDays}일</span>
                            </div>
                            <div>
                              <span className="text-[10px] text-emerald-700 font-bold block mb-0.5">복구 후 최종 잔여</span>
                              <span className="font-black text-sm text-emerald-700">{restoredBalance.toFixed(1)}일</span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="p-3.5 bg-gradient-to-r from-indigo-50 via-sky-50 to-emerald-50 rounded-2xl border border-indigo-100 space-y-2">
                          <div className="flex justify-between items-center text-xs">
                            <span className="font-extrabold text-indigo-950 flex items-center gap-1.5">
                              <span>📊</span> 연차 결재 승인 시 차감 계산
                            </span>
                            <span className="text-[11px] font-bold text-indigo-700 bg-white px-2 py-0.5 rounded-md border border-indigo-200 shadow-2xs">
                              {isSpecial ? '특별휴가 (차감 없음)' : `-${currentRequestDays}일 차감`}
                            </span>
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-xs bg-white p-2.5 rounded-xl border border-indigo-100 shadow-2xs text-center">
                            <div>
                              <span className="text-[10px] text-slate-400 font-bold block mb-0.5">신청 전 잔여</span>
                              <span className="font-black text-sm text-slate-900">{beforeBalance.toFixed(1)}일</span>
                            </div>
                            <div className="border-x border-slate-100">
                              <span className="text-[10px] text-indigo-600 font-bold block mb-0.5">이번 신청</span>
                              <span className="font-black text-sm text-indigo-600">{isSpecial ? '0일' : `-${currentRequestDays}일`}</span>
                            </div>
                            <div>
                              <span className="text-[10px] text-emerald-600 font-bold block mb-0.5">승인 후 최종 잔여</span>
                              <span className={`font-black text-sm ${afterBalance < 0 && !isSpecial ? 'text-rose-600' : 'text-emerald-600'}`}>
                                {afterBalance.toFixed(1)}일
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* 3. Leave Request Info Card */}
                      <div className="border border-slate-200 rounded-2xl p-4 space-y-2.5 bg-white">
                        <div className="flex justify-between items-center text-sm border-b border-slate-100 pb-2">
                          <span className="font-semibold text-slate-500 text-xs">휴가 유형</span>
                          <div className="font-bold text-xs">
                            {leaveTypeDisplay}
                          </div>
                        </div>
                        <div className="flex justify-between items-center text-sm border-b border-slate-100 pb-2">
                          <span className="font-semibold text-slate-500 text-xs">휴가 기간</span>
                          <span className="font-bold text-slate-800 text-xs">
                            {selectedDetailApproval.StartDate} {selectedDetailApproval.StartDate !== selectedDetailApproval.EndDate && ` ~ ${selectedDetailApproval.EndDate}`}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-sm border-b border-slate-100 pb-2">
                          <span className="font-semibold text-slate-500 text-xs">차감 영업일수</span>
                          <span className="font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded text-xs">
                            {selectedDetailApproval.LeaveDays || selectedDetailApproval.Days || 0} 일 차감
                          </span>
                        </div>
                        <div className="space-y-1 pt-1">
                          <span className="text-xs font-semibold text-slate-500 block">신청 사유</span>
                          <p className="text-xs text-slate-700 bg-slate-50 p-2.5 rounded-xl border border-slate-200 whitespace-pre-wrap leading-relaxed">
                            {selectedDetailApproval.Details || selectedDetailApproval.Reason}
                          </p>
                        </div>
                        {selectedDetailApproval.CancelReason && (
                          <div className="space-y-1 pt-1 bg-rose-50 p-2.5 rounded-xl border border-rose-200">
                            <span className="text-xs font-black text-rose-700 flex items-center gap-1">
                              <span>⚠️</span> 연차 취소 신청 사유
                            </span>
                            <p className="text-xs text-rose-900 bg-white p-2 rounded-lg border border-rose-100 font-medium">
                              {selectedDetailApproval.CancelReason}
                            </p>
                          </div>
                        )}
                        {/* Proof Attachment */}
                        <div className="pt-2 flex justify-between items-center text-sm border-t border-slate-100">
                          <span className="font-semibold text-slate-500 text-xs">증빙 첨부파일</span>
                          {selectedDetailApproval.ProofUrl ? (
                            <a 
                              href={selectedDetailApproval.ProofUrl} 
                              target="_blank" 
                              rel="noreferrer" 
                              className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-lg transition-colors inline-flex items-center gap-1.5"
                            >
                              <FileText className="w-3.5 h-3.5" />
                              <span>파일 보기</span>
                            </a>
                          ) : (
                            <span className="text-slate-400 font-bold text-xs">없음</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Rejection Reason (if rejected) */}
                {selectedDetailApproval.Status === 'REJECTED' && (
                  <div className="space-y-2">
                    <h4 className="text-xs font-extrabold text-slate-800 uppercase tracking-wider">반려 사유</h4>
                    <div className="p-4 bg-rose-50 border border-rose-200 rounded-2xl">
                      <p className="text-xs font-bold text-rose-800 leading-relaxed">
                        {selectedDetailApproval.RejectReason || '등록된 반려 사유가 없습니다.'}
                      </p>
                    </div>
                  </div>
                )}

                {/* Approval Stages (Approval Line) Progress */}
                <div className="space-y-3">
                  <h4 className="text-xs font-extrabold text-slate-800 uppercase tracking-wider">결재 진행 라인 현황</h4>
                  <div className="space-y-3">
                    {(selectedDetailApproval.ApprovalStages || []).map((stage, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs relative">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center font-bold text-[10px] flex-shrink-0 ${
                          stage.Status === 'APPROVED' ? 'bg-emerald-500 text-white' :
                          stage.Status === 'REJECTED' ? 'bg-rose-500 text-white' :
                          stage.Status === 'PENDING' ? 'bg-amber-500 text-white' :
                          'bg-slate-300 text-slate-600'
                        }`}>
                          {stage.Step}
                        </div>
                        <div className="flex-1 space-y-1">
                          <div className="flex justify-between items-center">
                            <span className="font-extrabold text-slate-800">
                              {stage.ApproverName} {stage.JobTitle || stage.ApproverJobTitle || ''} {stage.Department ? `(${stage.Department})` : ''}
                            </span>
                            <span className={`px-2 py-0.5 rounded text-[10px] font-extrabold ${
                              stage.Status === 'APPROVED' ? 'bg-emerald-100 text-emerald-800' :
                              stage.Status === 'REJECTED' ? 'bg-rose-100 text-rose-800' :
                              stage.Status === 'PENDING' ? 'bg-amber-100 text-amber-800' :
                              'bg-slate-200 text-slate-600'
                            }`}>
                              {stage.Status === 'APPROVED' ? '승인' : stage.Status === 'REJECTED' ? '반려' : stage.Status === 'PENDING' ? '결재 중' : '대기'}
                            </span>
                          </div>
                          {stage.Comment && (
                            <p className="text-slate-600 font-medium bg-white p-2 rounded-lg border border-slate-200 mt-1">
                              의견: {stage.Comment}
                            </p>
                          )}
                          {(stage.ProcessedAt || stage.ActionAt) && (
                            <span className="text-[10px] text-slate-400 block text-right pt-0.5">
                              처리 일시: {new Date(stage.ProcessedAt || stage.ActionAt).toLocaleString()}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 px-6 py-4 border-t border-slate-200 flex justify-end">
                <button
                  onClick={() => setSelectedDetailApproval(null)}
                  className="px-5 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold text-sm rounded-xl cursor-pointer"
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default LeaveApprovalManagement;
